/**
 * Delete-captain confirmation dialog.
 *
 * Renders a centered modal asking the admin to confirm deletion of a
 * specific captain. The component is intentionally narrow in scope:
 *
 *  - It does NOT remove the captain from any list. The parent stays in
 *    charge of the list state and only learns about a confirmed deletion
 *    via the `onConfirm` callback.
 *  - It DOES surface failures from the parent's delete operation: if
 *    `onConfirm` rejects, the dialog stays open and shows the Arabic error
 *    "تعذّر الحذف، يرجى المحاولة لاحقاً" inline (Req 8.6 — failed delete
 *    must not silently drop the row from the displayed list).
 *  - Buttons are disabled while the request is in flight to avoid double
 *    submissions.
 *  - Pressing Escape behaves like the "إلغاء" button (Req 7.4: confirm
 *    dialog presents a cancel option).
 *
 * The dialog is rendered inline — the parent decides where it lives in the
 * tree. When `open` is false, or `captain` is null, the component renders
 * nothing (returns `null`) so the overlay never traps focus or input.
 *
 * Validates: Requirements 7.4, 8.6
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';

import styles from './DeleteCaptainDialog.module.css';

const DELETE_FAILURE_MESSAGE = 'تعذّر الحذف، يرجى المحاولة لاحقاً';

export interface DeleteCaptainDialogProps {
  open: boolean;
  captain: { _id: string; name: string } | null;
  onConfirm: (id: string) => Promise<void>;
  onCancel: () => void;
}

export function DeleteCaptainDialog({
  open,
  captain,
  onConfirm,
  onCancel,
}: DeleteCaptainDialogProps): JSX.Element | null {
  const titleId = useId();
  const descriptionId = useId();
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Track whether the component is still mounted/visible so async work
  // started by `onConfirm` cannot call `setState` after the parent closes
  // the dialog.
  const activeRef = useRef(true);
  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
    };
  }, []);

  // Reset transient state every time the dialog is reopened or the target
  // captain changes — a previous failure should not leak across sessions.
  useEffect(() => {
    if (open) {
      setErrorMessage(null);
      setSubmitting(false);
    }
  }, [open, captain?._id]);

  const handleCancel = useCallback(() => {
    if (submitting) {
      return;
    }
    onCancel();
  }, [submitting, onCancel]);

  // Close-on-Escape handler. Bound at the document level only while the
  // dialog is open so it doesn't interfere with the rest of the admin UI.
  useEffect(() => {
    if (!open || captain === null) {
      return undefined;
    }
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        handleCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, captain, handleCancel]);

  if (!open || captain === null) {
    return null;
  }

  const handleConfirm = async (): Promise<void> => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      await onConfirm(captain._id);
      // The parent is responsible for closing the dialog (by toggling the
      // `open` prop) on a successful delete. We just clean up local state
      // in case it stays mounted briefly.
      if (activeRef.current) {
        setSubmitting(false);
      }
    } catch {
      // Failure path: keep the dialog open and show an inline error.
      // We deliberately do NOT call `onCancel` here — the caller's contract
      // is that `onCancel` represents an explicit user dismissal, not a
      // failed attempt (Req 8.6).
      if (activeRef.current) {
        setErrorMessage(DELETE_FAILURE_MESSAGE);
        setSubmitting(false);
      }
    }
  };

  return (
    <div
      className={styles.overlay}
      data-testid="delete-captain-dialog-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className={styles.card}
      >
        <h2 id={titleId} className={styles.title}>
          تأكيد الحذف
        </h2>
        <p id={descriptionId} className={styles.message}>
          {`هل أنت متأكد من حذف الكابتن ${captain.name}؟`}
        </p>
        {errorMessage !== null ? (
          <div role="alert" className={styles.error}>
            {errorMessage}
          </div>
        ) : null}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.confirmButton}
            onClick={handleConfirm}
            disabled={submitting}
          >
            تأكيد
          </button>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={handleCancel}
            disabled={submitting}
          >
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeleteCaptainDialog;
